import React from 'react';
import { withStyles, WithStyles, createStyles } from '@material-ui/core';
import Box from 'ha/ui/next/Box';

import * as SearchFilters from 'ha/types/SearchFilters';
import formatPrice from 'ha/utils/formatPrice';
import units from 'ha/theme/units';
import Row from 'ha/ui/next/Row';
import { withIntl, IntlProps } from 'ha/i18n';

import { RegistrationCountryCodes } from 'ha/constants/Filters';
import MobileFiltersTitle from './MobileFiltersTitle';
import MobileFiltersSummary from './MobileFiltersSummary';
import FiltersTags from './FiltersTags';
import TypeFilter from './TypeFilter';
import FurnitureFilter from './FurnitureFilter';
import BillsFilter from './BillsFilter';
import RegistrationFilter from './RegistrationFilter';
import RecentlyAddedFilter from './RecentlyAddedFilter';
import SuitableForFilter from './SuitableForFilter';
import MobileFiltersButton from './MobileFiltersButton';
import PriceFilter from './PriceFilter';
import RulesFilter from './RulesFilter';
import GenderFilter from './GenderFilter';

interface Props {
  searchParams: object;
  onSubmit: SearchFilters.FilterSubmit;
  loadPreviewNumberOfListings: (searchParams: {}) => void;
  clearPreviewNumberOfListings: () => void;
  onClose: () => void;
  priceFilter: SearchFilters.PriceFilterProps;
  typeFilter: SearchFilters.TypeFilterValue;
  furnitureFilter: SearchFilters.FurnitureFilterValue;
  billsFilter: SearchFilters.BillsFilterValue;
  suitableForFilter: SearchFilters.SuitableForFilterValue;
  registrationFilter: SearchFilters.RegistrationFilterValue;
  rulesFilter: SearchFilters.RulesFilterValue;
  recentlyAddedFilter: SearchFilters.RecentlyAddedFilterValue;
  roomsFilter: SearchFilters.RoomsFilterValue;
  genderFilter: SearchFilters.GenderFilterValue;
  countryCode: string;
  updateButtonText: string;
  priceFilterTitle: string;
}

interface State {
  types: SearchFilters.TypeFilterValue;
  furniture: SearchFilters.FurnitureFilterValue;
  bills: SearchFilters.BillsFilterValue;
  gender: SearchFilters.GenderFilterValue;
  suitableFor: SearchFilters.SuitableForFilterValue;
  registration: SearchFilters.RegistrationFilterValue;
  rules: SearchFilters.RulesFilterValue;
  recentlyAdded: SearchFilters.RecentlyAddedFilterValue;
  rooms: SearchFilters.RoomsFilterValue;
  price: SearchFilters.PriceFilterChange | null;
}

type FilterValues = Partial<State>;

const defaultFilters = {
  types: [],
  gender: [],
  suitableFor: [],
  rules: [],
  furniture: null,
  bills: null,
  registration: null,
  recentlyAdded: null,
  rooms: { bedroomCount: [] },
  price: null,
};

const styles = createStyles({
  wrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    fontSize: units.rem(1.125),
  },
  container: {
    paddingBottom: units.rem(4.5),
    width: '100%',
    maxHeight: '100%',
    overflowY: 'scroll' /* has to be scroll, not auto */,
    WebkitOverflowScrolling: 'touch',
  },
  section: {
    padding: units.rem(1),
  },
});

export class MobileFilters extends React.Component<
  Props & IntlProps & WithStyles<typeof styles>,
  State
> {
  state: State;

  constructor(props: Props & IntlProps & WithStyles<typeof styles>) {
    super(props);

    this.state = this.getStateFromProps();
  }

  getStateFromProps = () => {
    const types = this.props.typeFilter.filter(Boolean);

    return {
      types,
      furniture: this.props.furnitureFilter,
      bills: this.props.billsFilter,
      registration: this.props.registrationFilter,
      gender: this.props.genderFilter.filter(Boolean),
      rooms: this.props.roomsFilter,
      rules: this.props.rulesFilter.filter(Boolean),
      suitableFor: this.props.suitableForFilter,
      recentlyAdded: this.props.recentlyAddedFilter,
      price:
        this.props.priceFilter.selectedMin || this.props.priceFilter.selectedMax
          ? {
              min: this.props.priceFilter.selectedMin,
              max: this.props.priceFilter.selectedMax,
              currency: this.props.priceFilter.currency,
            }
          : null,
    };
  };

  componentWillUnmount() {
    this.props.clearPreviewNumberOfListings();
  }

  handleSubmit = () => {
    const { onSubmit, onClose, intl } = this.props;
    const { price } = this.state;
    const rate = this.getCurrencyRate();

    onSubmit({
      ...defaultFilters,
      ...this.state,
      price: {
        currency: price ? price.currency : null,
        min: price ? Math.floor(price.min * rate) : null,
        max: price ? Math.floor(price.max * rate) : null,
      },
      intl,
    });
    onClose();
  };

  updateFilters = (values: FilterValues) => {
    this.setState(
      state => ({
        ...state,
        ...values,
      }),
      () => {
        const { types, price } = this.state;

        const rate = this.getCurrencyRate();

        const params = {
          ...this.props.searchParams,
          ...this.state,
          categories: types,
          priceMin: price ? Math.floor(price.min * rate) : null,
          priceMax: price ? Math.floor(price.max * rate) : null,
          currency: price ? price.currency : null,
        };

        this.props.loadPreviewNumberOfListings(params);
      },
    );
  };

  handleRemoveFilters = () => {
    this.updateFilters(defaultFilters);
  };

  handleTypeFilterToggle = (
    type: SearchFilters.TypeValue,
    selected: boolean,
  ) => {
    const types = selected
      ? this.state.types.concat(type)
      : this.state.types.filter(current => type !== current);

    const rooms = types.includes(SearchFilters.TypeValue.APARTMENT)
      ? this.state.rooms
      : {
          bedroomCount: [],
        };

    this.updateFilters({ types, rooms });
  };

  handleRoomsFilterChange = (
    type: SearchFilters.RoomsValue,
    selected: boolean,
  ) => {
    const bedroomCount = selected
      ? this.state.rooms.bedroomCount.concat(type)
      : this.state.rooms.bedroomCount.filter(current => type !== current);

    const types = [...this.state.types];

    if (!types.includes(SearchFilters.TypeValue.APARTMENT)) {
      types.push(SearchFilters.TypeValue.APARTMENT);
    }

    this.updateFilters({
      types,
      rooms: {
        bedroomCount,
      },
    });
  };

  handleFurnitureFilterChange = (furniture: SearchFilters.FurnitureValue) => {
    this.updateFilters({ furniture });
  };

  handleBillsFilterChange = (bills: SearchFilters.BillsValue) => {
    this.updateFilters({ bills });
  };

  handleRegistrationFilterChange = (
    registration: SearchFilters.RegistrationValue,
  ) => {
    this.updateFilters({ registration });
  };

  handleRulesFilterToggle = (
    selection: SearchFilters.RulesValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ rules: this.state.rules.concat(selection) });
    } else {
      this.updateFilters({
        rules: this.state.rules.filter(current => selection !== current),
      });
    }
  };

  handleGenderFilterToggle = (
    selection: SearchFilters.GenderValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ gender: this.state.gender.concat(selection) });
    } else {
      this.updateFilters({
        gender: this.state.gender.filter(current => selection !== current),
      });
    }
  };

  handleRecentlyAddedFilterChange = (
    value: SearchFilters.RecentlyAddedFilterValue,
  ) => {
    this.updateFilters({
      recentlyAdded: value,
    });
  };

  handleSuitableForFilterToggle = (
    selection: SearchFilters.SuitableForValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({
        suitableFor: this.state.suitableFor.concat(selection),
      });
    } else {
      this.updateFilters({
        suitableFor: this.state.suitableFor.filter(
          current => selection !== current,
        ),
      });
    }
  };

  handlePriceFilterChange = (price: SearchFilters.PriceFilterChange) => {
    const { min: selectedMin, max: selectedMax, currency } = price;

    const {
      priceMin,
      trimmedPriceMin,
      priceMax,
      trimmedPriceMax,
    } = this.props.priceFilter;

    // If selected value is the same as trimmed value (max or min possible) use non-trimmed value.
    const min = selectedMin !== trimmedPriceMin ? selectedMin : priceMin;
    const max = selectedMax !== trimmedPriceMax ? selectedMax : priceMax;

    this.updateFilters({ price: { min, max, currency } });
  };

  handleFilterRemove = (filter: string, value?: string) => {
    if (filter === 'type') {
      const types = this.state.types.filter(current => value !== current);
      const rooms =
        value === SearchFilters.TypeValue.APARTMENT
          ? {
              bedroomCount: [],
            }
          : this.state.rooms;

      this.updateFilters({
        types,
        rooms,
      });
    } else if (filter === 'bedroomCount') {
      this.updateFilters({
        rooms: {
          bedroomCount: this.state.rooms.bedroomCount.filter(
            current => value !== current,
          ),
        },
      });
    } else if (filter === 'rules') {
      this.updateFilters({
        rules: this.state.rules.filter(current => value !== current),
      });
    } else if (filter === 'gender') {
      this.updateFilters({
        gender: this.state.gender.filter(current => value !== current),
      });
    } else if (filter === 'suitableFor') {
      this.updateFilters({
        suitableFor: this.state.suitableFor.filter(
          current => value !== current,
        ),
      });
    } else if (filter === 'furniture') {
      this.updateFilters({ furniture: null });
    } else if (filter === 'bills') {
      this.updateFilters({ bills: null });
    } else if (filter === 'registration') {
      this.updateFilters({ registration: null });
    } else if (filter === 'price') {
      this.updateFilters({ price: null });
    } else if (filter === 'recentlyAdded') {
      this.updateFilters({ recentlyAdded: null });
    }
  };

  getCurrencyRate = (): number => {
    const {
      priceFilter: { currencyRates, currency: from },
    } = this.props;
    const to = (this.state.price && this.state.price.currency) || from;

    const fromRate = currencyRates[from];
    const toRate = currencyRates[to];

    return toRate / fromRate;
  };

  priceLabelFormatter = (price: number): string => {
    const {
      currency: originalCurrency,
      trimmedPriceMax,
    } = this.props.priceFilter;
    const currency = this.state.price
      ? this.state.price.currency
      : originalCurrency;

    const rate = this.getCurrencyRate();
    const formatted = formatPrice(price * rate, currency);

    return price === trimmedPriceMax ? `${formatted}+` : formatted;
  };

  render() {
    const {
      classes,
      priceFilter,
      onClose,
      countryCode,
      updateButtonText,
      priceFilterTitle,
    } = this.props;

    const {
      types,
      furniture,
      bills,
      price,
      rooms,
      registration,
      suitableFor,
      recentlyAdded,
      rules,
      gender,
    } = this.state;

    const filterCount =
      types.length +
      (furniture ? 1 : 0) +
      (bills ? 1 : 0) +
      (registration ? 1 : 0) +
      rules.length +
      suitableFor.length +
      (recentlyAdded ? 1 : 0) +
      rooms.bedroomCount.length +
      (price ? 1 : 0);

    const rate = this.getCurrencyRate();

    return (
      <Row wrap="nowrap" direction="column" className={classes.wrapper}>
        <MobileFiltersTitle onClose={onClose} />

        <Row wrap="nowrap" direction="column" className={classes.container}>
          <MobileFiltersSummary
            filterCount={filterCount}
            onRemove={this.handleRemoveFilters}
          />

          <Box marginTop={1} paddingX={2}>
            <FiltersTags
              types={types}
              furniture={furniture}
              bills={bills}
              suitableFor={suitableFor}
              registration={registration}
              recentlyAdded={recentlyAdded}
              gender={gender}
              rooms={rooms}
              rate={rate}
              price={price}
              rules={rules}
              onRemove={this.handleFilterRemove}
            />
          </Box>

          <hr />

          <div className={classes.section}>
            <PriceFilter
              displayPriceInputs
              priceFilterTitle={priceFilterTitle}
              onPriceChange={this.handlePriceFilterChange}
              onCurrencyChange={this.handlePriceFilterChange}
              currency={price ? price.currency : priceFilter.currency}
              priceMin={priceFilter.trimmedPriceMin}
              priceMax={priceFilter.trimmedPriceMax}
              priceDistribution={priceFilter.trimmedPriceDistribution}
              selectedMin={price ? price.min : priceFilter.trimmedPriceMin}
              selectedMax={price ? price.max : priceFilter.trimmedPriceMax}
              rate={rate}
              priceLabelFormatter={this.priceLabelFormatter}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <TypeFilter
              types={types}
              onToggle={this.handleTypeFilterToggle}
              rooms={rooms}
              onRoomsFilterChange={this.handleRoomsFilterChange}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <FurnitureFilter
              furniture={furniture}
              onChange={this.handleFurnitureFilterChange}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <BillsFilter
              bills={bills}
              onChange={this.handleBillsFilterChange}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <SuitableForFilter
              suitableFor={suitableFor}
              onToggle={this.handleSuitableForFilterToggle}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <GenderFilter
              gender={gender}
              onToggle={this.handleGenderFilterToggle}
            />
          </div>

          <hr />

          <div className={classes.section}>
            <RulesFilter
              rules={rules}
              onToggle={this.handleRulesFilterToggle}
            />
          </div>

          {RegistrationCountryCodes.includes(countryCode) && (
            <React.Fragment>
              <hr />

              <div className={classes.section}>
                <RegistrationFilter
                  registration={registration}
                  onChange={this.handleRegistrationFilterChange}
                />
              </div>
            </React.Fragment>
          )}

          <hr />

          <div className={classes.section}>
            <RecentlyAddedFilter
              value={recentlyAdded}
              onChange={this.handleRecentlyAddedFilterChange}
            />
          </div>
        </Row>

        <MobileFiltersButton onSubmit={this.handleSubmit}>
          {updateButtonText}
        </MobileFiltersButton>
      </Row>
    );
  }
}

export default withStyles(styles)(withIntl(MobileFilters));
